import {EventEmitter, OnDestroy, OnInit, Output} from "@angular/core";
import {FormBuilder} from "@angular/forms";
import {merge, Subscription} from "rxjs";
import {DictionaryModel} from "../../../shared/models/dictionary.model";
import {AggregationGroupModel} from "../../../shared/models/search/aggregations/aggregation-group.model";
import {EmptySearchParamsFilterModel} from "../../../shared/models/search/params/filters/empty-search-params-filter.model";
import {ProductResultItemModel} from "../../../shared/models/search/results/items/product-result-item.model";
import {SearchResultModel} from "../../../shared/models/search/results/search-result.model";
import {CollectionFilterBaseForm} from "../../forms/collection-filter-base.form";
import {CollectionResultsFilter} from "../../models/search-results/filters/collection-results-filter";
import {SuggestionsSearchModel} from "../../models/suggestions-search.model";
import {CollectionSearchParamsFactoryService} from "../../services/factories/collection-search-params-factory.service";
import {SuggestionsService} from "../../services/suggestions.service";

export abstract class CollectionFilterDirective<TFilterForm extends CollectionFilterBaseForm<TParamsFilter>, TParamsFilter extends EmptySearchParamsFilterModel> implements OnInit, OnDestroy {
    public filterForm: TFilterForm;
    protected submitSubscription: Subscription;

    protected _filter: TParamsFilter;

    private _searchResult: SearchResultModel<ProductResultItemModel, CollectionResultsFilter>;

    public get filter(): TParamsFilter {
        return this._filter;
    }

    public set filter(value: TParamsFilter) {
        this._filter = value;
    }

    public get searchResult(): SearchResultModel<ProductResultItemModel, CollectionResultsFilter> {
        return this._searchResult;
    }

    public set searchResult(value: SearchResultModel<ProductResultItemModel, CollectionResultsFilter>) {
        this._searchResult = value;
    }

    public get hasResult(): boolean {
        return Boolean(this.searchResult);
    }

    public get isEmptyResult(): boolean {
        return !this.searchResult.items || !this.searchResult.items.length;
    }

    public suggestionsList: DictionaryModel<AggregationGroupModel> = {};

    @Output()
    public filterSubmitted: EventEmitter<TParamsFilter> = new EventEmitter<TParamsFilter>();

    @Output()
    public filterUpdated: EventEmitter<TParamsFilter> = new EventEmitter<TParamsFilter>();

    protected constructor(
        protected formBuilder: FormBuilder,
        protected searchParamsFactory: CollectionSearchParamsFactoryService,
        protected suggestionsService: SuggestionsService
    ) {
    }

    public ngOnInit(): void {
        this.filterForm = this.createFilterForm();

        this.submitSubscription = merge(
            this.filterForm.changeObservable,
            this.filterForm.submitObservable
        )
        .subscribe((filter: TParamsFilter) => this.filterSubmitted.emit(filter));
    }

    public ngOnDestroy(): void {
        if (this.submitSubscription) {
            this.submitSubscription.unsubscribe();
            this.submitSubscription = null;
        }
        if(this.filterForm) {
            this.filterForm.dispose();
        }
    }

    public clearForm(): void {
        this.filterForm.reset();
    }

    protected abstract createFilterForm(): TFilterForm;

    protected searchForSuggestions(params: SuggestionsSearchModel): void {
        this.suggestionsService.getSuggestions(params)
            .subscribe(
                (result: AggregationGroupModel) => {
                    this.suggestionsList[params.field] = result;
                },
                () => {
                });
    }
}
